import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Collapse,
  PaginationItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Chip,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import BoltRounded from "@mui/icons-material/BoltRounded";
import SearchOffRounded from "@mui/icons-material/SearchOffRounded";
import ClearIcon from "@mui/icons-material/Clear";
import { CustomDialogHandle } from "../components/CustomDialog";
import dayjs from "dayjs";
import {
  TransactionsTable,
  ColorRadioButtons,
  CustomDialog,
  CustomSnackbar,
  DocumentForm,
  EmptyState,
  Loading,
  PageHeader,
  TransactionDetails,
} from "../components";
import { TransactionCard } from "../components/TransactionCard";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { CustomSnackbarRef } from "../components/CustomSnackbar";
import Pagination from "@mui/material/Pagination";
import { useData } from "../hooks/useData";
import { Transaction } from "../utils/types";
import { useTheme } from "@mui/material";
import { useTransactions } from "../hooks/useTransactions";
import { Dayjs } from "dayjs";
import { useMedia } from "../context/MediaContext";
import {
  downloadFile,
  ExportFormat,
  refreshTransaction,
  sendCB,
} from "../utils/api";
import { resolveWsUrl } from "../utils/apiBase";
import { TransactionFeed } from "../utils/transactionFeed";
import { matchesClientFilters } from "../utils/transactionFeedFilters";
import { useAuthStore } from "../store/authStore";
import { reverseTranslate } from "../utils/currency";
import Filters from "../components/Filters";
/** В предпросмотре бэкенда нет — сокет не поднимаем. */
const isPreview = process.env.REACT_APP_UI_PREVIEW === "true";

const TransactionPage: React.FC = () => {
  // const [mode, setMode] = useState<"transactions" | "tickets">("transactions");
  const { isMobile600 } = useMedia();
  const theme = useTheme();
  const { providers, merchants } = useData();
  const snackbarRef = useRef<CustomSnackbarRef>(null);
  type TFilters = {
    search_query: string;
    type: string[];
    minsum: number | null;
    maxsum: number | null;
    merchid: number[];
    providerid: number[];
    status: string[];
    forMerch: boolean;
    createdFrom: Dayjs | null;
    createdTo: Dayjs | null;
    updatedFrom: Dayjs | null;
    updatedTo: Dayjs | null;
    currency: string;
    payment_method: string;
  };
  const [filterss, setFilters] = useState<TFilters>({
    search_query: "",
    type: [],
    merchid: [],
    minsum: null,
    maxsum: null,
    providerid: [],
    status: [],
    forMerch: false,
    createdFrom: null,
    createdTo: null,
    updatedFrom: null,
    currency: "",
    updatedTo: null,
    payment_method: "",
  });

  const dateKeys: (keyof TFilters)[] = [
    "createdFrom",
    "createdTo",
    "updatedFrom",
    "updatedTo",
  ];
  const providerMap = useMemo(() => {
    const map = new Map();
    providers.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [providers]);

  const merchantMap = useMemo(() => {
    const map = new Map();
    merchants.forEach((m) => map.set(m.id, m.name));
    return map;
  }, [merchants]);

  const prepareFilters = () => {
    const prepared = {} as Partial<
      Record<keyof TFilters, TFilters[keyof TFilters]>
    >;

    (Object.keys(filterss) as (keyof TFilters)[]).forEach((key) => {
      const value = filterss[key];

      if (key === "forMerch") return;
      if (key === "currency" && value !== "") {
        prepared[key] = reverseTranslate(value).toLowerCase();
        return;
      }
      if (dateKeys.includes(key)) {
        if (value) {
          prepared[key] = (
            dayjs(value as any).isValid()
              ? dayjs(value as any).format("DD.MM.YYYY")
              : value
          ) as any;
        }
        return;
      }

      if (Array.isArray(value)) {
        if (value.length > 0) {
          prepared[key] = value as any;
        }
        return;
      }

      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed !== "") {
          prepared[key] = trimmed as any;
        }
        return;
      }

      if (typeof value === "boolean") {
        prepared[key] = value as any;
        return;
      }

      if (value !== null && value !== undefined) {
        prepared[key] = value as any;
      }
    });

    return Object.keys(prepared).length > 0
      ? (prepared as Partial<TFilters>)
      : null;
  };
  /**
   * Перечитывает открытую операцию. Живая лента не покрывает режим поиска, да и
   * карточку могли открыть и оставить надолго — здесь обновление по требованию.
   * Строка в списке обновляется заодно, чтобы данные не разошлись с карточкой.
   */
  const handleDetailsRefresh = async (id: string) => {
    try {
      const fresh = await refreshTransaction(id);
      if (!fresh) return false;

      setDetailsTransaction(fresh);
      setTransactions((rows) =>
        rows.map((row) => (row.id === fresh.id ? { ...row, ...fresh } : row)),
      );
      return true;
    } catch {
      return false;
    }
  };

  const sendCallBack = async (id: string) => {
    try {
      const res = await sendCB(id);
      if (res.data?.status === "success") {
        snackbarRef?.current?.showSnackbar("Успешно", "success");
      } else {
        snackbarRef?.current?.showSnackbar("Ошибка!", "error");
      }
    } catch (err) {
      console.error("Ошибка:", err);
      snackbarRef?.current?.showSnackbar("Ошибка!", "error");
    }
  };

  const downloadCSV = (data: {
    type: string[];
    merchid: number[];
    status: string[];
    forMerch: boolean;
    createdFrom: Dayjs | null;
    createdTo: Dayjs | null;
    updatedFrom: Dayjs | null;
    updatedTo: Dayjs | null;
    format?: ExportFormat;
  }) => {
    const {
      status,
      createdFrom,
      createdTo,
      type,
      merchid,
      forMerch,
      updatedFrom,
      updatedTo,
    } = data;
    snackbarRef?.current?.showSnackbar("Загрузка началась", "info");

    const params = {
      status,
      type,
      merchid,
      forMerch,
      createdFrom,
      createdTo,
      updatedFrom,
      updatedTo,
      format: data.format ?? "csv",
    };

    return downloadFile(params)
      .then((res) => {
        // Ответ приходит Blob: xlsx бинарный, и разбор его как текста портит файл.
        const blob: Blob = res.data;
        if (!blob || blob.size === 0) {
          snackbarRef?.current?.showSnackbar(
            "Не найдено данных за период",
            "warning",
          );

          return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", `transactions.${data.format ?? "csv"}`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error(error);
        snackbarRef?.current?.showSnackbar("Ошибка", "error");
      })
      .finally(() =>
        setFilters({
          search_query: "",
          type: [],
          merchid: [],
          providerid: [],
          minsum: null,
          maxsum: null,
          status: [],
          forMerch: false,
          createdFrom: null,
          createdTo: null,
          updatedFrom: null,
          currency: "",
          updatedTo: null,
          payment_method: "",
        }),
      );
  };
  const {
    transactions,
    total,
    loading,
    error,
    fetchRecentTrans,
    fetchTransactions,
    handleStatusChange,
    handleAmountChange,
    page,
    setPage,
    totalPage,
    setTransactions,
    setTotal,
  } = useTransactions(snackbarRef, prepareFilters());
  const logout = useAuthStore((state) => state.logout);
  const [selectedTransaction, setselectedTransaction] =
    useState<Transaction | null>(null);
  const [changeType, setChangeType] = useState<string>("amount");
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [detailsTransaction, setDetailsTransaction] =
    useState<Transaction | null>(null);
  const [feedStatus, setFeedStatus] = useState<
    "offline" | "online" | "denied"
  >("offline");
  const [lastEventAt, setLastEventAt] = useState<Date | null>(null);
  const [newCount, setNewCount] = useState<number>(0);

  // Обработчик ленты живёт всё время подключения, а страница и фильтры
  // меняются. Через ref он всегда видит их текущее значение и при этом не
  // заставляет пересоздавать сокет.
  const pageRef = useRef(page);
  pageRef.current = page;
  const filtersRef = useRef(filterss);
  filtersRef.current = filterss;

  const searchActive = filterss.search_query.trim() !== "";

  /**
   * Серверные фильтры ленты. Сокет фиксирует их на момент подключения, поэтому
   * их изменение обязано переподключать соединение — отсюда отдельный ключ.
   */
  const feedQuery = useMemo(() => {
    const query = new URLSearchParams();
    const put = (key: string, values: (string | number)[]) => {
      if (values.length) query.set(key, values.join(","));
    };

    put("merch_id", filterss.merchid);
    put("payment_terminal_id", filterss.providerid);
    put("status", filterss.status);
    put("transaction_type", filterss.type);
    if (filterss.payment_method) {
      query.set("payment_method", filterss.payment_method);
    }
    if (filterss.currency) {
      query.set("currency", reverseTranslate(filterss.currency).toLowerCase());
    }
    return query.toString();
  }, [
    filterss.merchid,
    filterss.providerid,
    filterss.status,
    filterss.type,
    filterss.payment_method,
    filterss.currency,
  ]);

  /**
   * Живая лента транзакций (WSS /transactions/ws).
   *
   * Порядок важен: сокет открывается раньше списка, события копятся и
   * применяются после загрузки. Иначе транзакции, созданные за время запроса,
   * потерялись бы — строка висела бы неверной до перезагрузки страницы.
   *
   * В режиме поиска лента не подключается: результат поиска приходит другой
   * ручкой и своим срезом, вставлять в него события нельзя.
   */
  useEffect(() => {
    if (isPreview || searchActive) return;

    const feed = new TransactionFeed(
      `${resolveWsUrl("/transactions/ws")}${feedQuery ? `?${feedQuery}` : ""}`,
      {
        onReady: async () => {
          await fetchRecentTrans(pageRef.current, "фильтры", prepareFilters());
          setNewCount(0);
          feed.drain();
        },
        onEvent: (event) => {
          if (event.type === "lagged") {
            // Накопленное потеряно, состояние списка недостоверно.
            snackbarRef.current?.showSnackbar(
              "Соединение отставало, список перезапрошен",
              "warning",
            );
            fetchRecentTrans(pageRef.current, "фильтры", prepareFilters());
            return;
          }

          setLastEventAt(new Date());

          if (event.type === "updated") {
            setTransactions((rows) => {
              const index = rows.findIndex((r) => r.id === event.transaction.id);
              if (index === -1) return rows;

              // Слияние, а не подмена: в событии приходит только заплатка.
              const merged = {
                ...rows[index],
                ...event.transaction,
              } as Transaction;

              // Проверяем уже слитую строку, а не заплатку: в ней может не быть
              // полей, по которым фильтруем, и строка исчезла бы напрасно.
              if (!matchesClientFilters(merged, filtersRef.current)) {
                return rows.filter((r) => r.id !== event.transaction.id);
              }

              const next = [...rows];
              next[index] = merged;
              return next;
            });
            return;
          }

          if (event.type === "created") {
            if (!matchesClientFilters(event.transaction, filtersRef.current)) {
              return;
            }
            // На второй странице и дальше вставка сверху сдвинула бы срез, и
            // при листании поехали бы строки. Показываем счётчик.
            if (pageRef.current !== 1) {
              setNewCount((count) => count + 1);
              return;
            }
            setTransactions((rows) => {
              if (rows.some((r) => r.id === event.transaction.id)) return rows;
              return [event.transaction as Transaction, ...rows].slice(0, 20);
            });
            setTotal((value) => value + 1);
          }
        },
        onAuthLost: () => {
          setFeedStatus("denied");
          logout();
        },
        onForbidden: () => {
          setFeedStatus("denied");
          snackbarRef.current?.showSnackbar(
            "Нет доступа к живой ленте транзакций",
            "error",
          );
        },
        onRejected: (reason) => {
          setFeedStatus("denied");
          console.error("Лента транзакций отклонена:", reason);
        },
        onStatus: (status) => setFeedStatus(status),
      },
    );

    feed.connect();
    return () => feed.close();
    // prepareFilters пересоздаётся на каждый рендер и в зависимости не идёт:
    // иначе сокет переподключался бы бесконечно.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedQuery, searchActive]);

  const dialogRef = useRef<CustomDialogHandle>(null);
  type FilterKey = keyof TFilters;

  const handleChange = useCallback(
    <K extends FilterKey>(
      key: K,
      eventOrValue: React.ChangeEvent<{ value: unknown }> | TFilters[K],
    ) => {
      const value: TFilters[K] =
        typeof eventOrValue === "object" &&
        eventOrValue !== null &&
        "target" in eventOrValue
          ? ((eventOrValue as React.ChangeEvent<{ value: unknown }>).target
              .value as TFilters[K])
          : (eventOrValue as TFilters[K]);

      setFilters((prevFilters) => {
        const updatedFilters: TFilters = { ...prevFilters };

        if (!Array.isArray(value) && (key === "status" || key === "type")) {
          updatedFilters[key] = [value] as TFilters[K];
        } else if (key === "merchid" && Array.isArray(value)) {
          updatedFilters[key] = (value as string[])
            .map(
              (merchName) =>
                merchants.find((merch) => merch.name === merchName)?.id,
            )
            .filter(Boolean) as TFilters[K];
        } else if (key === "providerid" && Array.isArray(value)) {
          updatedFilters[key] = (value as string[])
            .map(
              (provName) =>
                providers.find((prov) => prov.name === provName)?.id,
            )
            .filter(Boolean) as TFilters[K];
        } else {
          updatedFilters[key] = value;
        }

        return updatedFilters;
      });
    },
    [merchants],
  );
  const runSearch = () => {
    setPage(1);
    if (filterss.search_query.trim() !== "") {
      fetchTransactions(filterss);
    } else {
      fetchRecentTrans(1, "страница");
    }
  };

  const searchBar = (
    <Box
      component="form"
      role="search"
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        runSearch();
      }}
      sx={{
        display: "flex",
        alignItems: { xs: "stretch", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5,
        mt: "15px",
      }}
    >
      <TextField
        label="Поиск по ID"
        name="search_query"
        fullWidth
        value={filterss.search_query}
        onChange={(e) =>
          setFilters((prev) => ({
            ...prev,
            search_query: e.target.value,
          }))
        }
        sx={{
          "& input:-webkit-autofill": {
            boxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
            fontSize: "1rem",
          },
        }}
        slotProps={{
          htmlInput: {
            style: {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          },
          input: {
            endAdornment: filterss.search_query ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Очистить поиск"
                  size="small"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, search_query: "" }))
                  }
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        sx={{
          height: "56px",
          flexShrink: 0,
          whiteSpace: "nowrap",
          minWidth: { sm: 200 },
        }}
        disabled={loading}
      >
        {filterss.search_query !== "" ? "Применить" : "Загрузить последние"}
      </Button>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          width: "100%",
          paddingTop: "25px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          // Layout отдаёт странице контейнер с overflow: hidden. Без minHeight: 0
          // колонка не сжимается, и всё, что не влезло, срезается — первой
          // пагинация, она идёт последней.
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "96%",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            margin: "0 auto",
            paddingBottom: 1.5,
          }}
        >
          <PageHeader
            title="Транзакции"
            description="Поиск, контроль статусов и управление операциями"
            icon={ReceiptLongOutlined}
            actions={
              <>
                {!isPreview && (
                  <Tooltip
                    title={
                      searchActive
                        ? "В режиме поиска лента не обновляется"
                        : feedStatus === "online"
                        ? lastEventAt
                          ? `Последнее событие в ${lastEventAt.toLocaleTimeString(
                              "ru-RU",
                            )}`
                          : "Соединение установлено, ждём событий"
                        : feedStatus === "denied"
                        ? "Лента недоступна"
                        : "Соединение потеряно, идёт переподключение"
                    }
                  >
                    <Chip
                      icon={<BoltRounded />}
                      label={
                        searchActive
                          ? "Поиск"
                          : feedStatus === "online"
                          ? "Live"
                          : feedStatus === "denied"
                          ? "Нет ленты"
                          : "Связь..."
                      }
                      size="small"
                      variant={feedStatus === "online" ? "filled" : "outlined"}
                      color={
                        searchActive || feedStatus === "denied"
                          ? "default"
                          : feedStatus === "online"
                          ? "success"
                          : "warning"
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  </Tooltip>
                )}
                <Button
                  variant="contained"
                  startIcon={<DownloadRounded />}
                  onClick={() => {
                    setChangeType("export");
                    dialogRef.current?.openDialog();
                  }}
                >
                  Скачать список
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterAltIcon />}
                  onClick={() => setOpenFilters(true)}
                >
                  Фильтры
                </Button>
                {isMobile600 && (
                  <IconButton
                    aria-label="Показать быстрый поиск"
                    onClick={() => setSettingsOpen(!settingsOpen)}
                  >
                    <SettingsIcon />
                  </IconButton>
                )}
              </>
            }
          />
          <Filters
            merchants={merchants}
            providers={providers}
            setFilters={setFilters}
            filterss={filterss}
            handleChange={handleChange}
            openFilters={openFilters}
            setOpenFilters={setOpenFilters}
            fetchRecentTrans={() =>
              fetchRecentTrans(page, "фильтры", prepareFilters())
            }
          />
          {isMobile600 ? (
            <Collapse in={settingsOpen}>{searchBar}</Collapse>
          ) : (
            searchBar
          )}
          <Box
            sx={{
              backgroundColor: "transparent",
              // Список занимает остаток высоты, а не долю экрана: прежние
              // 64vh складывались с шапкой и поиском и выдавливали пагинацию
              // за нижнюю границу окна.
              flex: 1,
              minHeight: 0,
              marginTop: "25px",
              borderRadius: "15px",
              overflowY: isMobile600 ? "auto" : "hidden",
              overflowX: "hidden",
            }}
          >
            {loading ? (
              <Loading compact variant="table" />
            ) : transactions.length > 0 && isMobile600 ? (
              <Box display={"flex"} flexDirection={"column"} gap={"5px"}>
                {transactions.map((t) => (
                  <TransactionCard
                    key={t.id}
                    t={t}
                    merchName={merchantMap.get(t.merch_id)}
                    providerName={providerMap.get(t.payment_terminal_id)}
                    idType={"merchant"}
                    onEdit={() => {
                      setselectedTransaction(t);
                      setChangeType("amount");
                      dialogRef.current?.openDialog();
                    }}
                    onCallback={() => sendCallBack(t.id)}
                    onOpenDetails={() => setDetailsTransaction(t)}
                  />
                ))}
              </Box>
            ) : transactions.length > 0 && !isMobile600 ? (
              <TransactionsTable
                onOpenDetails={setDetailsTransaction}
                setTransaction={setselectedTransaction}
                dialogRef={dialogRef}
                transactions={transactions}
                providers={providers}
                setChangeType={setChangeType}
                merchants={merchants}
                sendCallBack={sendCallBack}
              />
            ) : (
              <EmptyState
                icon={SearchOffRounded}
                title="Транзакции не найдены"
                description={
                  error
                    ? error
                    : "Измените параметры поиска или загрузите последние операции."
                }
                compact
                action={
                  <Button
                    variant="contained"
                    onClick={() => {
                      const pageToUse = 1;
                      fetchRecentTrans(pageToUse, "страница");
                    }}
                  >
                    Загрузить последние
                  </Button>
                }
              />
            )}
          </Box>
          {newCount > 0 && (
            // На странице кроме первой новые операции не вставляются: вставка
            // сверху сдвинула бы срез и при листании поехали бы строки.
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2,
                flexShrink: 0,
              }}
            >
              <Button
                size="small"
                variant="contained"
                startIcon={<BoltRounded />}
                onClick={() => {
                  setNewCount(0);
                  setPage(1);
                }}
              >
                {`${newCount} новых — к первой странице`}
              </Button>
            </Box>
          )}
          {total > 20 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: isMobile600 ? "center" : "start",
                mt: 2,
                // Пагинация не сжимается: место ей выделяется раньше списка.
                flexShrink: 0,
              }}
            >
              <Pagination
                count={totalPage}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                variant="outlined"
                shape="rounded"
                size="large"
                siblingCount={1}
                boundaryCount={isMobile600 ? 0 : 1}
                renderItem={(item) => {
                  if (
                    item.type === "start-ellipsis" ||
                    item.type === "end-ellipsis"
                  ) {
                    return null;
                  }
                  return <PaginationItem {...item} />;
                }}
                sx={{
                  "& .MuiPaginationItem-root": { fontSize: "15px" },
                }}
              />
            </Box>
          ) : null}
        </Box>
      </Box>
      <TransactionDetails
        transaction={detailsTransaction}
        merchantName={
          detailsTransaction
            ? merchantMap.get(detailsTransaction.merch_id)
            : undefined
        }
        onRefresh={handleDetailsRefresh}
        onClose={() => setDetailsTransaction(null)}
      />

      <CustomSnackbar duration={3000} ref={snackbarRef} />
      <CustomDialog
        ref={dialogRef}
        dialog={{
          title:
            changeType === "export"
              ? "Параметры для выгрузки"
              : "Изменения данных",
          context: null,
          sx: {
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {changeType === "export" ? (
          <DocumentForm
            setFilters={setFilters}
            filterss={filterss}
            handleChange={handleChange}
            merchants={merchants}
            downloadCSV={downloadCSV}
            dialogRef={dialogRef}
          />
        ) : null}
        {selectedTransaction &&
          (changeType === "status" || changeType === "amount") && (
            <Box width={"100%"}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={changeType}
                onChange={(_, value) => value && setChangeType(value)}
                aria-label="Что изменить"
                sx={{ width: "90%", margin: "0 auto 25px", display: "flex" }}
              >
                <ToggleButton value="amount" sx={{ flex: 1 }}>
                  Сумма
                </ToggleButton>
                <ToggleButton value="status" sx={{ flex: 1 }}>
                  Статус
                </ToggleButton>
              </ToggleButtonGroup>
              {changeType === "amount" ? (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <TextField
                    type="number"
                    variant="outlined"
                    size="small"
                    label="Введите сумму"
                    value={selectedTransaction?.paid_amount}
                    onChange={(e) => {
                      if (!selectedTransaction) return;
                      const value = Number(e.target.value);
                      if (isNaN(value)) return;
                      setselectedTransaction(
                        (prev) => prev && { ...prev, paid_amount: value },
                      );
                    }}
                    helperText={
                      <span
                        style={{
                          color:
                            (selectedTransaction?.paid_amount ?? 0) > 0
                              ? "#4caf50"
                              : "#999",
                          fontWeight: 500,
                        }}
                      >
                        Оплаченная сумма:{" "}
                        {(selectedTransaction &&
                          transactions.find(
                            (el) => el.id === selectedTransaction.id,
                          )?.paid_amount) ??
                          0}{" "}
                      </span>
                    }
                    sx={{ width: "90%" }}
                  />
                </Box>
              ) : changeType === "status" ? (
                <ColorRadioButtons
                  selectedValue={selectedTransaction.status}
                  setSelectedValue={setselectedTransaction}
                />
              ) : null}
              <Box
                sx={{
                  width: "90%",
                  margin: "25px auto 0",
                  display: "flex",
                  gap: 1.5,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => dialogRef?.current?.closeDialog()}
                  sx={{ flex: 1, height: 48 }}
                >
                  Отмена
                </Button>
                <Button
                  variant="contained"
                  autoFocus
                  onClick={() => {
                    changeType === "status"
                      ? handleStatusChange(
                          selectedTransaction.status,
                          selectedTransaction.id,
                        )
                      : handleAmountChange(
                          Number(selectedTransaction.paid_amount),
                          selectedTransaction.id,
                        );
                    dialogRef?.current?.closeDialog();
                  }}
                  sx={{ flex: 1, height: 48 }}
                >
                  Сохранить
                </Button>
              </Box>
            </Box>
          )}
      </CustomDialog>
    </>
  );
};

export default TransactionPage;
